// client/src/pages/DataAnalytics/DataAnalytics.js
/**
 * Analytics-vyn med flikar: SymptomSearch (exakt kombinationsmatch via
 * $setIsSubset), TrendAnalysis, RegionalComparison och SymptomCorrelation.
 */
import API_CONFIG from '../../config/apiConfig';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_SYMPTOMS } from '../../components/shared/constants/symptoms';
import TrendAnalysis from './components/TrendAnalysis';
import RegionalComparison from './components/RegionalComparison';
import SymptomCorrelation from './components/SymptomCorrelation';

import './DataAnalytics.css';

// ✅ OPTIMIZED: SymptomSearch with API-first approach
const SymptomSearch = React.memo(() => {
  const { t } = useTranslation();

  // ✅ Symptom translation helpers
  const tSymptom = (name) => t(`symptoms.${name}`, name);
  const tCombination = (comboString) => {
    if (!comboString) return '';
    return comboString.split('+').map(s => tSymptom(s.trim())).join('+');
  };

  const [selectedWeek, setSelectedWeek] = useState(53);
  const availableWeeks = [40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53];
  
  // ✅ DIRECT: Use central symptoms list
  const availableSymptoms = ALL_SYMPTOMS;
  
  // Local state for symptom search
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [minMatchCount, setMinMatchCount] = useState(null); // null = alla, eller antal
  const [geographicScope, setGeographicScope] = useState('neighborhood');
  const [postalCodeSearch, setPostalCodeSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Beräkna similarityThreshold från minMatchCount
  const similarityThreshold = useMemo(() => {
    const total = selectedSymptoms.length;
    if (total < 3) return 100; // Kräv alla om < 3 symptom
    if (minMatchCount === null) return 100; // Default: alla
    return Math.round((minMatchCount / total) * 100);
  }, [selectedSymptoms.length, minMatchCount]);

  // Reset minMatchCount när antal symptom ändras
  useEffect(() => {
    if (selectedSymptoms.length >= 3) {
      setMinMatchCount(selectedSymptoms.length); // Default: alla
    } else {
      setMinMatchCount(null);
    }
  }, [selectedSymptoms.length]);

  // Auto-fill city from postal code (simple mapping)
  const autoCity = useMemo(() => {
    if (!postalCodeSearch) return '';
    
    const postalPrefix = postalCodeSearch.substring(0, 3);
    const cityMappings = {
      '211': 'Malmö',
      '112': 'Stockholm', 
      '111': 'Stockholm',
      '413': 'Göteborg',
      '411': 'Göteborg'
    };
    
    return cityMappings[postalPrefix] || '';
  }, [postalCodeSearch]);

  // Handle symptom toggle
  const handleSymptomToggle = useCallback((symptom) => {
    const symptomName = typeof symptom === 'string' ? symptom : symptom.name || symptom._id;
    
    setSelectedSymptoms(prev => {
      return prev.includes(symptomName)
        ? prev.filter(s => s !== symptomName)
        : [...prev, symptomName];
    });
  }, []);

  // Execute search by calling API with symptoms parameter
  const executeSearch = useCallback(async () => {
    if (selectedSymptoms.length === 0) {
      alert(t('dataAnalytics.search.alertSelectSymptom'));
      return;
    }

    setSearchLoading(true);
    setSearchResults(null);

    try {
      console.log(`🔍 DataAnalytics: SYMPTOM SEARCH via API`);
      console.log(`   - Symptoms: [${selectedSymptoms.join(', ')}]`);
      console.log(`   - Min match count: ${minMatchCount}/${selectedSymptoms.length}`);
      console.log(`   - Geographic scope: ${geographicScope}`);

      // Build API URL with symptoms parameter
      const symptomsParam = selectedSymptoms.join(',');
      let apiUrl = `${API_CONFIG.baseUrl}/health-data/postal-groups?week=${selectedWeek}&symptoms=${encodeURIComponent(symptomsParam)}`;
      
      // ✅ NEW: Add minMatchCount for partial matching (2 of 3, etc.)
      if (minMatchCount !== null && minMatchCount < selectedSymptoms.length) {
        apiUrl += `&minMatchCount=${minMatchCount}`;
      }
      
      // Add geographic filter and groupBy
      if (geographicScope === 'neighborhood' && postalCodeSearch) {
        apiUrl += `&postalCode=${postalCodeSearch.trim().replace(/\s+/g, '')}`;
      } else if (geographicScope === 'city') {
        if (cityFilter || autoCity) {
          const targetCity = cityFilter || autoCity;
          apiUrl += `&city=${encodeURIComponent(targetCity)}`;
        }
        apiUrl += `&groupBy=city`;
      } else if (geographicScope === 'country') {
        apiUrl += `&groupBy=country`;
      }
      
      console.log(`   - API URL: ${apiUrl}`);
      
      const response = await fetch(apiUrl);
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API request failed');
      }
      
      console.log(`   - API returned ${result.data?.length || 0} postal groups`);
      console.log(`   - Metadata:`, result.metadata);
      
      // Transform API results to search results format
      const matches = (result.data || []).map(group => ({
        postalCode: group.postalCode,
        city: group.city || 'Unknown',
        country: group.country || 'Sverige',
        symptomCombination: selectedSymptoms.join('+'),
        matchedSymptoms: selectedSymptoms.map(symptom => ({ name: symptom, count: group.symptomCount || group.count || 0 })),
        // ✅ FIXED: Use matchingCount from API response
        matchingCount: group.matchingCount || selectedSymptoms.length,
        similarity: group.matchingCount >= selectedSymptoms.length ? 100 : Math.round((group.matchingCount / selectedSymptoms.length) * 100),
        count: group.symptomCount || group.count || 0,
        totalReports: group.totalCount || group.count || 0,
        exactMatch: group.matchingCount >= selectedSymptoms.length
      })).filter(match => match.count > 0)
        .sort((a, b) => b.count - a.count);

      const searchResult = {
        matches: matches,
        summary: {
          totalMatches: matches.length,
          totalSymptomReports: matches.reduce((sum, match) => sum + match.count, 0),
          averageSimilarity: 100
        },
        debugInfo: {
          totalServerGroups: result.data?.length || 0,
          processedLocally: false,
          serverAggregated: true,
          apiMetadata: result.metadata
        }
      };

      setSearchResults(searchResult);
      setSearchPerformed(true);

      console.log(`✅ DataAnalytics: SYMPTOM SEARCH complete`);
      console.log(`   - Matches found: ${matches.length}`);
      console.log(`   - Total symptom reports: ${searchResult.summary.totalSymptomReports}`);

    } catch (error) {
      console.error('❌ Error in symptom search:', error);
      alert(t('dataAnalytics.search.alertSearchError', { message: error.message }));
    } finally {
      setSearchLoading(false);
    }
  }, [selectedSymptoms, selectedWeek, minMatchCount, geographicScope, postalCodeSearch, cityFilter, autoCity, t]);

  // Preset management with localStorage
  const DEFAULT_PRESETS = [
    { id: 1, name: 'Influensa', symptoms: ['Feber', 'Hosta', 'Huvudvärk'] },
    { id: 2, name: 'Förkylning', symptoms: ['Snuva', 'Hosta'] },
    { id: 3, name: 'Magsjuka', symptoms: ['Illamående', 'Kräkningar'] }
  ];
  
  const PRESETS_STORAGE_KEY = 'dataAnalytics_symptomPresets';
  const MAX_PRESETS = 5;
  
  // Load presets from localStorage
  const loadPresets = useCallback(() => {
    try {
      const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Could not load presets from localStorage:', e);
    }
    return DEFAULT_PRESETS;
  }, []);
  
  const [presets, setPresets] = useState(loadPresets);
  const [showPresetManager, setShowPresetManager] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingSymptoms, setEditingSymptoms] = useState([]);
  
  // Save presets to localStorage
  const savePresets = useCallback((newPresets) => {
    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(newPresets));
    } catch (e) {
      console.warn('Could not save presets to localStorage:', e);
    }
  }, []);
  
  // Apply preset (select its symptoms)
  const applyPreset = useCallback((symptoms) => {
    setSelectedSymptoms(symptoms);
  }, []);
  
  // Start editing a preset
  const startEditPreset = useCallback((preset) => {
    setEditingPresetId(preset.id);
    setEditingName(preset.name);
    setEditingSymptoms([...preset.symptoms]);
  }, []);
  
  // Cancel editing
  const cancelEditPreset = useCallback(() => {
    setEditingPresetId(null);
    setEditingName('');
    setEditingSymptoms([]);
  }, []);
  
  // Save edited preset
  const saveEditPreset = useCallback(() => {
    if (!editingName.trim()) {
      alert(t('dataAnalytics.search.alertPresetName'));
      return;
    }
    if (editingSymptoms.length === 0) {
      alert(t('dataAnalytics.search.alertSelectOneSymptom'));
      return;
    }
    
    const newPresets = presets.map(p => 
      p.id === editingPresetId 
        ? { ...p, name: editingName.trim(), symptoms: editingSymptoms }
        : p
    );
    setPresets(newPresets);
    savePresets(newPresets);
    cancelEditPreset();
  }, [presets, editingPresetId, editingName, editingSymptoms, savePresets, cancelEditPreset, t]);
  
  // Delete preset
  const deletePreset = useCallback((id) => {
    const newPresets = presets.filter(p => p.id !== id);
    setPresets(newPresets);
    savePresets(newPresets);
    if (editingPresetId === id) {
      cancelEditPreset();
    }
  }, [presets, editingPresetId, savePresets, cancelEditPreset]);
  
  // Add new preset
  const addNewPreset = useCallback(() => {
    if (presets.length >= MAX_PRESETS) {
      alert(t('dataAnalytics.search.alertMaxPresets', { max: MAX_PRESETS }));
      return;
    }
    const newId = Math.max(0, ...presets.map(p => p.id)) + 1;
    const newPreset = { id: newId, name: t('dataAnalytics.search.newPreset'), symptoms: [] };
    const newPresets = [...presets, newPreset];
    setPresets(newPresets);
    savePresets(newPresets);
    startEditPreset(newPreset);
  }, [presets, savePresets, startEditPreset, t]);
  
  // Reset to defaults
  const resetPresetsToDefault = useCallback(() => {
    if (window.confirm(t('dataAnalytics.search.confirmResetPresets'))) {
      setPresets(DEFAULT_PRESETS);
      savePresets(DEFAULT_PRESETS);
      cancelEditPreset();
    }
  }, [savePresets, cancelEditPreset, t]);
  
  // Toggle symptom in editing mode
  const toggleEditingSymptom = useCallback((symptomName) => {
    setEditingSymptoms(prev => 
      prev.includes(symptomName)
        ? prev.filter(s => s !== symptomName)
        : [...prev, symptomName]
    );
  }, []);

  return (
    <div className="symptom-search">
      <h2 style={{ marginTop: 0 }}>{t('dataAnalytics.search.title')}</h2>
      
      {/* Search Controls */}
      <div className="search-controls">
        <h3>{t('dataAnalytics.search.filters')}</h3>
        
        {/* Symptom Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            {t('dataAnalytics.search.selectSymptoms', { count: selectedSymptoms.length })}
          </label>
          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            justifyContent: 'center',
            maxHeight: '150px',
            overflowY: 'auto',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}>
            {availableSymptoms.map((symptom, index) => {
              const symptomName = typeof symptom === 'string' ? symptom : symptom.name || symptom._id;
              const isSelected = selectedSymptoms.includes(symptomName);
              
              return (
                <div key={`${symptomName}-${index}`} style={{ 
                  padding: '5px 10px',
                  backgroundColor: isSelected ? '#2196f3' : '#e0e0e0',
                  color: isSelected ? 'white' : 'black',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  userSelect: 'none'
                }} onClick={() => handleSymptomToggle(symptom)}>
                  {tSymptom(symptomName)}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Presets with expandable manager */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label style={{ fontWeight: 'bold', marginRight: '10px' }}>
              {t('dataAnalytics.search.quickSelect')}
            </label>
            <button
              onClick={() => setShowPresetManager(!showPresetManager)}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#666'
              }}
              title={t('dataAnalytics.search.manageTooltip')}
            >
              ⚙️ {t('dataAnalytics.search.manage')}
            </button>
          </div>
          
          {/* Preset buttons */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: showPresetManager ? '15px' : '0' }}>
            {presets.map((preset) => (
              <button
                key={preset.id}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f1f8e9',
                  border: '1px solid #c5e1a5',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => applyPreset(preset.symptoms)}
                title={preset.symptoms.map(s => tSymptom(s)).join(', ')}
              >
                {preset.name}
              </button>
            ))}
            {presets.length === 0 && (
              <span style={{ color: '#999', fontStyle: 'italic' }}>{t('dataAnalytics.search.noPresets')}</span>
            )}
          </div>
          
          {/* Expandable preset manager */}
          {showPresetManager && (
            <div style={{
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '15px',
              marginTop: '10px'
            }}>
              <div style={{ marginBottom: '15px' }}>
                {presets.map((preset) => (
                  <div key={preset.id} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '10px',
                    backgroundColor: editingPresetId === preset.id ? '#e3f2fd' : '#f9f9f9',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    border: editingPresetId === preset.id ? '1px solid #2196f3' : '1px solid #eee'
                  }}>
                    {editingPresetId === preset.id ? (
                      /* Editing mode */
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          placeholder={t('dataAnalytics.search.presetNamePlaceholder')}
                          style={{
                            width: '200px',
                            padding: '6px 10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            marginBottom: '10px',
                            fontSize: '14px'
                          }}
                        />
                        <div style={{ marginBottom: '10px' }}>
                          <small style={{ color: '#666', display: 'block', marginBottom: '5px' }}>
                            {t('dataAnalytics.search.selectSymptomsEdit', { count: editingSymptoms.length })}
                          </small>
                          <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '5px',
                            maxHeight: '100px',
                            overflowY: 'auto',
                            padding: '5px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: '#fff'
                          }}>
                            {availableSymptoms.map((symptom, idx) => {
                              const symptomName = typeof symptom === 'string' ? symptom : symptom.name;
                              const isSelected = editingSymptoms.includes(symptomName);
                              return (
                                <div
                                  key={idx}
                                  onClick={() => toggleEditingSymptom(symptomName)}
                                  style={{
                                    padding: '3px 8px',
                                    backgroundColor: isSelected ? '#4caf50' : '#e0e0e0',
                                    color: isSelected ? 'white' : 'black',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    userSelect: 'none'
                                  }}
                                >
                                  {tSymptom(symptomName)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={saveEditPreset}
                            style={{
                              padding: '5px 12px',
                              backgroundColor: '#4caf50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {t('dataAnalytics.search.save')}
                          </button>
                          <button
                            onClick={cancelEditPreset}
                            style={{
                              padding: '5px 12px',
                              backgroundColor: '#9e9e9e',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {t('dataAnalytics.search.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display mode */
                      <>
                        <div style={{ flex: 1 }}>
                          <strong style={{ fontSize: '14px' }}>{preset.name}</strong>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                            {preset.symptoms.map(s => tSymptom(s)).join(', ')}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={() => startEditPreset(preset)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#fff',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title={t('dataAnalytics.search.editTooltip')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deletePreset(preset.id)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#fff',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title={t('dataAnalytics.search.deleteTooltip')}
                          >
                            🗑️
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                <button
                  onClick={addNewPreset}
                  disabled={presets.length >= MAX_PRESETS}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: presets.length >= MAX_PRESETS ? '#e0e0e0' : '#2196f3',
                    color: presets.length >= MAX_PRESETS ? '#999' : 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: presets.length >= MAX_PRESETS ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {presets.length >= MAX_PRESETS 
                    ? t('dataAnalytics.search.addNewMax', { max: MAX_PRESETS })
                    : t('dataAnalytics.search.addNew')
                  }
                </button>
                <button
                  onClick={resetPresetsToDefault}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#fff',
                    color: '#666',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {t('dataAnalytics.search.resetDefault')}
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Search Options */}
        <div style={{ 
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {t('dataAnalytics.search.matching')}
            </label>
            {selectedSymptoms.length < 3 ? (
              <div className="matching-info-box">
                {t('dataAnalytics.search.matchingInfo')}
              </div>
            ) : (
              <select
                value={minMatchCount || selectedSymptoms.length}
                onChange={(e) => setMinMatchCount(parseInt(e.target.value))}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                {/* Generera alternativ dynamiskt */}
                <option value={selectedSymptoms.length}>
                  {t('dataAnalytics.search.allSymptoms', { count: selectedSymptoms.length })}
                </option>
                {selectedSymptoms.length >= 3 && (
                  <option value={selectedSymptoms.length - 1}>
                    {t('dataAnalytics.search.atLeast', { min: selectedSymptoms.length - 1, total: selectedSymptoms.length })}
                  </option>
                )}
                {selectedSymptoms.length >= 4 && (
                  <option value={Math.ceil(selectedSymptoms.length / 2)}>
                    {t('dataAnalytics.search.atLeast', { min: Math.ceil(selectedSymptoms.length / 2), total: selectedSymptoms.length })}
                  </option>
                )}
              </select>
            )}
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {t('dataAnalytics.search.geographicArea')}
            </label>
            <select
              value={geographicScope}
              onChange={(e) => setGeographicScope(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="neighborhood">{t('dataAnalytics.search.postalCode')}</option>
              <option value="city">{t('dataAnalytics.search.city')}</option>
              <option value="country">{t('dataAnalytics.search.country')}</option>
            </select>
          </div>
        </div>
        
        {/* Location Search */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            {t('dataAnalytics.search.postalCodeLabel')}
          </label>
          <input
            type="text"
            value={postalCodeSearch}
            onChange={(e) => setPostalCodeSearch(e.target.value)}
            placeholder={t('dataAnalytics.search.postalCodePlaceholder')}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
          {autoCity && (
            <div style={{ marginTop: '5px', fontSize: '14px', color: '#666' }}>
              {t('dataAnalytics.search.autoCity')} <strong>{autoCity}</strong>
            </div>
          )}
        </div>
        
        {/* Search Button */}
        <button
          onClick={executeSearch}
          disabled={selectedSymptoms.length === 0 || searchLoading}
          className="search-button"
        >
          {searchLoading ? t('dataAnalytics.search.searching') : t('dataAnalytics.search.searchButton')}
        </button>
      </div>

      {/* Search Results */}
      {searchPerformed && (
        <div className="search-results">
          <h3>{t('dataAnalytics.search.results')}</h3>
          
          {/* Results Summary */}
          <div className="results-summary">
            <p className="summary-main">
              {t('dataAnalytics.search.foundAreas', { 
                count: searchResults?.matches?.length || 0,
                matchDesc: minMatchCount === selectedSymptoms.length 
                  ? t('dataAnalytics.search.allOf', { count: selectedSymptoms.length })
                  : t('dataAnalytics.search.atLeastOf', { min: minMatchCount, total: selectedSymptoms.length })
              })}
              {geographicScope === 'neighborhood' && postalCodeSearch && ` ${t('dataAnalytics.search.inPostalCode', { code: postalCodeSearch })}`}
              {geographicScope === 'city' && autoCity && ` ${t('dataAnalytics.search.inCity', { city: autoCity })}`}
              {geographicScope === 'country' && ` ${t('dataAnalytics.search.inSweden')}`}
            </p>
            <p className="summary-meta">
              {t('dataAnalytics.search.reportCount', { count: searchResults?.summary?.totalSymptomReports || 0 })} • 
              {t('dataAnalytics.search.weekInfo', { week: selectedWeek })}
            </p>
          </div>
          
          {/* Results Table */}
          {searchResults && searchResults.matches && searchResults.matches.length > 0 ? (
            <div className="results-table-wrapper">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>{t('dataAnalytics.search.thPostalCode')}</th>
                    <th>{t('dataAnalytics.search.thSymptoms')}</th>
                    <th>{t('dataAnalytics.search.thMatching')}</th>
                    <th>{t('dataAnalytics.search.thReports')}</th>
                    <th>{t('dataAnalytics.search.thCity')}</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.matches.slice(0, 50).map((match, index) => (
                    <tr key={`${match.postalCode}-${match.symptomCombination}-${index}`}>
                      <td className="cell-postal">{match.postalCode}</td>
                      <td className="cell-symptom">{tCombination(match.symptomCombination) || 'Unknown'}</td>
                      <td className="cell-match">
                        <span className={`match-badge ${minMatchCount >= selectedSymptoms.length ? 'exact' : 'partial'}`}>
                          {minMatchCount < selectedSymptoms.length ? '≥' : ''}{minMatchCount}/{selectedSymptoms.length}
                        </span>
                      </td>
                      <td className="cell-count">{match.count}</td>
                      <td className="cell-city">{match.city}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {searchResults.matches.length > 50 && (
                <div className="results-truncated">
                  {t('dataAnalytics.search.showingTop', { total: searchResults.matches.length })}
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <p><strong>{t('dataAnalytics.search.noResults', { week: selectedWeek })}</strong></p>
              <p>{t('dataAnalytics.search.noResultsHint')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ✅ OPTIMIZED: Main content component with server aggregation
const DataAnalyticsContent = React.memo(() => {
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState(53);
  const availableWeeks = [40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53];
  
  const [activeView, setActiveView] = useState('search');

  const handleViewChange = useCallback((view) => {
    setActiveView(view);
  }, []);

  const handleWeekChange = useCallback((week) => {
    setSelectedWeek(Number(week));
  }, []);

  return (
    <div className="data-analytics-container">
      <h1 className="analytics-header">{t('dataAnalytics.title')}</h1>

      {/* Info Panel för Smittskyddsexperter */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #ddd'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: '16px', color: '#333' }}>
              {t('dataAnalytics.advancedAnalysis')}
            </strong>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '8px', lineHeight: '1.5' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>{t('dataAnalytics.infoSimilarSymptoms')}</strong> {t('dataAnalytics.infoSimilarDesc')}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>{t('dataAnalytics.infoTrends')}</strong> {t('dataAnalytics.infoTrendsDesc')}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>{t('dataAnalytics.infoRegional')}</strong> {t('dataAnalytics.infoRegionalDesc')}
              </p>
              <p style={{ margin: '0' }}>
                <strong>{t('dataAnalytics.infoCorrelation')}</strong> {t('dataAnalytics.infoCorrelationDesc')}
              </p>
            </div>
          </div>
          <div style={{ marginLeft: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>{t('dataAnalytics.weekLabel')}</label>
            <select 
              value={selectedWeek || ''}
              onChange={(e) => handleWeekChange(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: 'white',
                fontWeight: 'bold'
              }}
            >
              {availableWeeks && availableWeeks.map(week => {
                const weekNumber = typeof week === 'object' ? week.week : week;
                return (
                  <option key={weekNumber} value={weekNumber}>{t('dataAnalytics.weekOption', { week: weekNumber })}</option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="analytics-navigation" style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => handleViewChange('search')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeView === 'search' ? '#2196f3' : '#e0e0e0',
            color: activeView === 'search' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          {t('dataAnalytics.navSearch')}
        </button>
        
        <button 
          onClick={() => handleViewChange('trends')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeView === 'trends' ? '#2196f3' : '#e0e0e0',
            color: activeView === 'trends' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          {t('dataAnalytics.navTrends')}
        </button>
        
        <button 
          onClick={() => handleViewChange('regional')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeView === 'regional' ? '#2196f3' : '#e0e0e0',
            color: activeView === 'regional' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          {t('dataAnalytics.navRegional')}
        </button>
        
        <button 
          onClick={() => handleViewChange('correlation')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeView === 'correlation' ? '#2196f3' : '#e0e0e0',
            color: activeView === 'correlation' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          {t('dataAnalytics.navCorrelation')}
        </button>
      </div>

      {/* Content */}
      <div className="analysis-content">
        {activeView === 'search' && <SymptomSearch />}
        
        {activeView === 'trends' && <TrendAnalysis />}
        
        {activeView === 'regional' && (
          <RegionalComparison />
        )}
        
        {activeView === 'correlation' && <SymptomCorrelation />}
      </div>
    </div>
  );
});

// ✅ MAIN EXPORT: Completely standalone DataAnalytics (no HealthDataProvider needed)
const DataAnalyticsWithProvider = () => {
  return <DataAnalyticsContent />;
};

export default DataAnalyticsWithProvider;
